import { useMemo } from "react";

interface Transaction {
  id: string;
  type: "income" | "expense";
  amount: number;
  account?: string | null;      // legacy text field (pre-migration), may be absent
  account_id?: string | null;   // current UUID field (post-migration)
  date: string;
  is_backdated?: boolean;
}

interface Account {
  id: string;
  name: string;
  initial_balance: number;
  balance_as_of_date?: string | null;
}

interface Wallet {
  id: string;
  account_id: string;
  name: string;
  balance: number;
}

interface BalanceResult {
  accountBalances: Map<string, number>; // accountId -> calculated balance
  walletBalances: Map<string, number>;  // walletId -> calculated balance
  getAccountBalance: (account: Account) => number;
  getWalletBalance: (wallet: Wallet, parentAccountName: string) => number;
}

/**
 * Calculate account and wallet balances from transactions
 * Transactions with format "AccountName" affect the account
 * Transactions with format "AccountName → WalletName" affect the wallet
 */
export const useAccountBalances = (
  transactions: Transaction[],
  accounts: Account[],
  wallets: Wallet[]
): BalanceResult => {
  return useMemo(() => {
    // Build account name to ID map and balance_as_of_date map
    const accountNameToId = new Map<string, string>();
    const accountIdToName = new Map<string, string>();
    const accountBalanceAsOfDate = new Map<string, string | null>();
    accounts.forEach(acc => {
      accountNameToId.set(acc.name, acc.id);
      accountIdToName.set(acc.id, acc.name);
      accountBalanceAsOfDate.set(acc.id, acc.balance_as_of_date || null);
    });
    
    // Build wallet lookup: "AccountName → WalletName" -> walletId
    // Also map wallet to parent account's balance_as_of_date
    const walletKeyToId = new Map<string, string>();
    const walletIdToParentAccountId = new Map<string, string>();
    wallets.forEach(wallet => {
      const parentName = accountIdToName.get(wallet.account_id);
      if (parentName) {
        const key = `${parentName} → ${wallet.name}`;
        walletKeyToId.set(key, wallet.id);
        walletIdToParentAccountId.set(wallet.id, wallet.account_id);
      }
    });
    
    // Calculate transaction totals per account and wallet
    const accountTransactionTotals = new Map<string, number>(); // accountId -> net change
    const walletTransactionTotals = new Map<string, number>();  // walletId -> net change
    
    transactions.forEach(t => {
      // Skip backdated transactions
      if (t.is_backdated) return;

      const amount = t.type === "income" ? Number(t.amount) : -Number(t.amount);

      if (t.account) {
        // Legacy transactions: account is a text field ("AccountName" or "AccountName → WalletName")
        if (t.account.includes(" → ")) {
          const walletId = walletKeyToId.get(t.account);
          if (walletId) {
            // Check if transaction is before parent account's balance_as_of_date
            const parentAccountId = walletIdToParentAccountId.get(walletId);
            if (parentAccountId) {
              const balanceDate = accountBalanceAsOfDate.get(parentAccountId);
              if (balanceDate && t.date < balanceDate) {
                return;
              }
            }
            const current = walletTransactionTotals.get(walletId) || 0;
            walletTransactionTotals.set(walletId, current + amount);
          }
        } else {
          const accountId = accountNameToId.get(t.account);
          if (accountId) {
            const balanceDate = accountBalanceAsOfDate.get(accountId);
            if (balanceDate && t.date < balanceDate) {
              return;
            }
            const current = accountTransactionTotals.get(accountId) || 0;
            accountTransactionTotals.set(accountId, current + amount);
          }
        }
      } else if (t.account_id) {
        // New transactions: account_id is a UUID referencing accounts(id)
        const accountId = t.account_id;
        const balanceDate = accountBalanceAsOfDate.get(accountId);
        if (balanceDate && t.date < balanceDate) {
          return;
        }
        const current = accountTransactionTotals.get(accountId) || 0;
        accountTransactionTotals.set(accountId, current + amount);
      }
    });
    
    // Calculate final balances
    const accountBalances = new Map<string, number>();
    accounts.forEach(acc => {
      const transactionDelta = accountTransactionTotals.get(acc.id) || 0;
      accountBalances.set(acc.id, acc.initial_balance + transactionDelta);
    });
    
    const walletBalances = new Map<string, number>();
    wallets.forEach(wallet => {
      const transactionDelta = walletTransactionTotals.get(wallet.id) || 0;
      walletBalances.set(wallet.id, wallet.balance + transactionDelta);
    });
    
    const getAccountBalance = (account: Account): number => {
      return accountBalances.get(account.id) ?? account.initial_balance;
    };
    
    const getWalletBalance = (wallet: Wallet, parentAccountName: string): number => {
      return walletBalances.get(wallet.id) ?? wallet.balance;
    };
    
    return {
      accountBalances,
      walletBalances,
      getAccountBalance,
      getWalletBalance,
    };
  }, [transactions, accounts, wallets]);
};
